import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Box, IconButton, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { hideToast, subscribeToasts } from "../utils/toastManager";

const SEVERITY_STYLES = {
  success: { accent: "#16a34a", Icon: CheckCircleRoundedIcon },
  error: { accent: "#dc2626", Icon: ErrorRoundedIcon },
  warning: { accent: "#d97706", Icon: WarningRoundedIcon },
  info: { accent: "#2563eb", Icon: InfoRoundedIcon },
};

function ToastThumb({ toast }) {
  const src = toast.image;
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
  }, [src]);

  if (!src || broken) {
    return (
      <Box
        sx={{
          width: 56,
          height: 72,
          flexShrink: 0,
          borderRadius: 1.5,
          bgcolor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6b7280",
        }}
      >
        <ShoppingBagRoundedIcon sx={{ fontSize: 22 }} />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={src}
      alt=""
      onError={() => setBroken(true)}
      sx={{
        width: 56,
        height: 72,
        flexShrink: 0,
        objectFit: "cover",
        borderRadius: 1.5,
        bgcolor: "#f3f4f6",
      }}
    />
  );
}

function ToastCard({ toast }) {
  const { accent, Icon } = SEVERITY_STYLES[toast.severity] || SEVERITY_STYLES.success;
  const hasProduct = Boolean(toast.productName || toast.image);
  const metaBits = [];
  if (toast.size) metaBits.push(`Size ${toast.size}`);
  if (toast.quantity != null) {
    metaBits.push(`Qty ${toast.quantity}`);
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: "flex",
        alignItems: "stretch",
        minWidth: 280,
        maxWidth: 360,
        bgcolor: "#fff",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: "0 18px 40px rgba(15, 23, 42, 0.16)",
        border: "1px solid rgba(15, 23, 42, 0.06)",
        pointerEvents: "auto",
      }}
    >
      <Box sx={{ width: 4, bgcolor: accent, flexShrink: 0 }} />
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          alignItems: "center",
          px: 1.5,
          py: 1.25,
          flex: 1,
          minWidth: 0,
        }}
      >
        {hasProduct ? (
          <ToastThumb toast={toast} />
        ) : (
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: "50%",
              bgcolor: `${accent}14`,
              color: accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ fontSize: 22 }} />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {hasProduct ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
                <Icon sx={{ fontSize: 14, color: accent, flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: accent,
                    lineHeight: 1.2,
                  }}
                >
                  {toast.title}
                </Typography>
              </Box>
              {toast.productName ? (
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#111827",
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {toast.productName}
                </Typography>
              ) : null}
            </>
          ) : (
            <>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#111827",
                  lineHeight: 1.3,
                }}
              >
                {toast.title}
              </Typography>
              {toast.message ? (
                <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25, lineHeight: 1.35 }}>
                  {toast.message}
                </Typography>
              ) : null}
            </>
          )}
          {metaBits.length > 0 && (
            <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
              {metaBits.join(" · ")}
            </Typography>
          )}
          {toast.message && hasProduct ? (
            <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
              {toast.message}
            </Typography>
          ) : null}
          {/* The toast's one action (showToast `action`). Pressing it runs
              the handler and dismisses the toast — the exit animation is
              the "slide back" the shopper sees. In the card's own palette
              (literal, light) rather than ob-* tokens: the card is a
              literal white surface in both themes, and a token button
              would flip to light ink on it in dark mode. */}
          {toast.action ? (
            <Box
              component="button"
              type="button"
              onClick={() => {
                try {
                  toast.action.onClick?.();
                } catch (err) {
                  console.warn("[toast] action threw", err);
                }
                hideToast(toast.id);
              }}
              sx={{
                mt: 1,
                px: 1.5,
                py: 0.6,
                border: "none",
                borderRadius: 99,
                bgcolor: "#111827",
                color: "#fff",
                fontSize: 12.5,
                fontWeight: 700,
                lineHeight: 1.2,
                letterSpacing: "0.01em",
                cursor: "pointer",
                transition: "background-color 0.15s ease, transform 0.15s ease",
                "&:hover": { bgcolor: "#1f2937" },
                "&:active": { transform: "scale(0.97)" },
                "&:focus-visible": {
                  outline: "2px solid #111827",
                  outlineOffset: 2,
                },
              }}
            >
              {toast.action.label}
            </Box>
          ) : null}
        </Box>
        <IconButton
          size="small"
          aria-label="Dismiss notification"
          onClick={() => hideToast(toast.id)}
          sx={{ color: "#9ca3af", p: 0.5, ml: 0.5, alignSelf: "flex-start" }}
        >
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

/**
 * Mount once near the app root. Renders toasts from `showToast()`.
 */
export default function ToastProvider() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    return subscribeToasts(setItems);
  }, []);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 80,
        right: 16,
        zIndex: 14000,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        pointerEvents: "none",
      }}
    >
      <AnimatePresence initial={false}>
        {items.map((toast) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, x: 28, y: -6 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 20, y: -4 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            <ToastCard toast={toast} />
          </motion.div>
        ))}
      </AnimatePresence>
    </Box>
  );
}
